import {
  DatabaseAuto,
  IautomaticItemTax,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  TautomaticItemTaxRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Represents an automatic item tax configuration in the system.
 * This class provides methods to manage automatic tax application to items,
 * including creation, retrieval, updating, and filtering of tax rules.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TautomaticItemTax - The type definition for automatic item tax properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link AutomaticItemTax} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const automaticItemTax = await AutomaticItemTax.getAll();
 *
 * const automaticItemTax = await AutomaticItemTax.filterAll({
 *
 * });
 *
 * const automaticItemTax = await AutomaticItemTax.bulkDelete({
 *
 * });
 *
 * const automaticItemTax = await AutomaticItemTax.getOne('resourceId');
 *
 * const automaticItemTax = await AutomaticItemTax.remove();
 *
 * ```
 */
export class AutomaticItemTax extends DatabaseAuto {
  /** Base API endpoint for automatic item tax operations */
  static readonly baseUrl = '/auto-item-tax';

  /** The tax rate as a percentage (e.g., 10 for 10%) */
  readonly percentage: IautomaticItemTax['percentage'];

  /** The name of the tax (e.g., "VAT", "Sales Tax") */
  readonly name: IautomaticItemTax['name'];

  /** The entity enforcing this tax (e.g., "Federal", "State") */
  readonly enforcer: IautomaticItemTax['enforcer'];

  /** Optional reason for the tax */
  readonly reason?: IautomaticItemTax['reason'];

  /** Whether this tax rule is currently active */
  readonly active: IautomaticItemTax['active'];

  /** Array of item IDs where this tax should be exclusively applied */
  readonly includeOnlyIds: IautomaticItemTax['includeOnlyIds'] = [];

  /** Array of item IDs where this tax should not be applied */
  readonly excludeIds: IautomaticItemTax['excludeIds'] = [];

  /**
   * Creates a new instance of AutomaticItemTax.
   * @param {IautomaticItemTax} data - The automatic item tax configuration data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: IautomaticItemTax) {
    validateData(data, {
      percentage: {
        validate: (v) => {
          return typeof v === 'number';
        },
        message: 'percentage must be a number'
      },
      name: {
        validate: (v) => {
          return typeof v === 'string';
        },
        message: 'name must be a string'
      },
      enforcer: {
        validate: (v) => {
          return typeof v === 'string';
        },
        message: 'enforcer must be a string'
      },
      reason: {
        required: false,
        validate: (v) => {
          return typeof v === 'string';
        },
        message: 'reason must be a string'
      }
    });

    super(data);

    this.percentage = data.percentage;
    this.name = data.name;
    this.enforcer = data.enforcer;
    this.reason = data.reason;
    this.active = data.active;
    this.includeOnlyIds = data.includeOnlyIds || [];
    this.excludeIds = data.excludeIds || [];
  }

  /**
   * Retrieves all automatic item tax configurations with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: AutomaticItemTax[] }>}
   * An object containing the total count and an array of automatic item tax configurations
   * @throws {Error} If there's an error fetching the tax configurations
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IautomaticItemTax>>(`${AutomaticItemTax.baseUrl}?${query}`);

    const automaticItemTaxes = await lastValueFrom(observer$);

    return {
      count: automaticItemTaxes.count,
      dataArr: automaticItemTaxes.data.map((val) => new AutomaticItemTax(val))
    };
  }

  /**
   * Filters automatic item tax configurations based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: AutomaticItemTax[] }>}
   * An object containing the total count and an array of filtered tax configurations
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IautomaticItemTax>>(
        `${AutomaticItemTax.baseUrl}/filter`,
        filter
      );

    const automaticItemTaxes = await lastValueFrom(observer$);

    return {
      count: automaticItemTaxes.count,
      dataArr: automaticItemTaxes.data.map((val) => new AutomaticItemTax(val))
    };
  }

  /**
   * Retrieves a single automatic item tax configuration by its ID.
   * @param {string} resourceId - The unique identifier of the tax configuration to retrieve
   * @returns {Promise<AutomaticItemTax>} The requested automatic item tax configuration
   * @throws {Error} If the tax configuration is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IautomaticItemTax>(`${AutomaticItemTax.baseUrl}/${resourceId}`);
    const automaticItemTax = await lastValueFrom(observer$);

    return new AutomaticItemTax(automaticItemTax);
  }

  /**
   * Creates a new automatic item tax configuration.
   * @param {TautomaticItemTaxRequest<true>} payload - The tax configuration data to create
   * @returns {Promise<AutomaticItemTax>} The newly created automatic item tax configuration
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: TautomaticItemTaxRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<IautomaticItemTax>(
        `${AutomaticItemTax.baseUrl}`,
        payload
      );

    return new AutomaticItemTax(await lastValueFrom(observer$));
  }

  /**
   * Updates the current automatic item tax configuration with new values.
   * @param {TautomaticItemTaxRequest} payload - The updated tax configuration data
   * @returns {Promise<AutomaticItemTax>} The updated automatic item tax configuration
   * @throws {Error} If the update operation fails or the tax configuration doesn't exist
   */
  async update(payload: TautomaticItemTaxRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<IautomaticItemTax>(
        `${AutomaticItemTax.baseUrl}/${this._id}`,
        payload
      );

    return new AutomaticItemTax(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current automatic item tax configuration.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} If the deletion fails or the tax configuration doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${AutomaticItemTax.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
